import {useEffect, useState} from 'react';
import {useNavigate} from 'react-router-dom';
import {App, Button, Form, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tag} from 'antd';
import {DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, ReloadOutlined} from '@ant-design/icons';
import type {ColumnsType} from 'antd/es/table';
import {WorkflowBinding, workflowBindingService} from '../../services/workflowBindingService';

const WORKFLOW_TYPES = [
    {value: 'MARKET_ANALYSIS', label: '市场分析'},
    {value: 'TRADE_EXECUTION', label: '交易执行'},
];

const WorkflowBindingsPage: React.FC = () => {
    const {message: messageApi} = App.useApp();
    const navigate = useNavigate();
    const [bindings, setBindings] = useState<WorkflowBinding[]>([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);
    const [form] = Form.useForm();

    const loadBindings = async () => {
        setLoading(true);
        try {
            const data = await workflowBindingService.getAll();
            setBindings(data);
        } catch {
            messageApi.error('加载绑定列表失败');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadBindings();
    }, []);

    const handleAdd = () => {
        setEditingId(null);
        form.resetFields();
        form.setFieldsValue({workflowType: 'MARKET_ANALYSIS', isActive: 1});
        setModalVisible(true);
    };

    const handleEdit = (record: WorkflowBinding) => {
        setEditingId(record.id!);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            if (editingId) {
                await workflowBindingService.update(editingId, values);
                messageApi.success('更新成功');
            } else {
                await workflowBindingService.create(values);
                messageApi.success('创建成功');
            }
            setModalVisible(false);
            loadBindings();
        } catch (e: any) {
            if (e?.errorFields) return;
            messageApi.error(e?.message || '操作失败');
        }
    };

    const handleToggle = async (record: WorkflowBinding, checked: boolean) => {
        try {
            await workflowBindingService.update(record.id!, {isActive: checked ? 1 : 0});
            messageApi.success(checked ? '已启用' : '已禁用');
            loadBindings();
        } catch {
            messageApi.error('操作失败');
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await workflowBindingService.delete(id);
            messageApi.success('删除成功');
            loadBindings();
        } catch {
            messageApi.error('删除失败');
        }
    };

    const columns: ColumnsType<WorkflowBinding> = [
        {title: 'ID', dataIndex: 'id', key: 'id', width: 80},
        {
            title: '工作流类型', dataIndex: 'workflowType', key: 'workflowType', width: 140,
            render: (v: string) => {
                const t = WORKFLOW_TYPES.find(w => w.value === v);
                return <Tag color={v === 'MARKET_ANALYSIS' ? 'blue' : 'orange'}>{t?.label || v}</Tag>;
            }
        },
        {title: '用户ID', dataIndex: 'userId', key: 'userId', width: 100},
        {
            title: 'API连接', dataIndex: 'apiConnectionId', key: 'apiConnectionId', width: 120,
            render: (v: number | null) => null == v ? <Tag>默认</Tag> : <Tag color="green">{v}</Tag>
        },
        {
            title: '状态', dataIndex: 'isActive', key: 'isActive', width: 80,
            render: (v: number, record: WorkflowBinding) => (
                <Switch checked={1 === v} size="small"
                        onChange={(checked: boolean) => handleToggle(record, checked)}/>
            )
        },
        {
            title: '更新时间', dataIndex: 'updatedAt', key: 'updatedAt', width: 180,
            render: (v: string) => v ? new Date(v).toLocaleString() : '-'
        },
        {
            title: '操作', key: 'action', width: 160, fixed: 'right',
            render: (_: unknown, record: WorkflowBinding) => (
                <Space>
                    <Button type="link" size="small" icon={<EditOutlined/>}
                            onClick={() => handleEdit(record)}/>
                    <Button type="link" size="small" icon={<EyeOutlined/>}
                            onClick={() => navigate('/executions')}
                            title="查看执行记录"/>
                    <Popconfirm title="确定删除？" onConfirm={() => handleDelete(record.id!)}>
                        <Button type="link" size="small" danger icon={<DeleteOutlined/>}/>
                    </Popconfirm>
                </Space>
            )
        },
    ];

    return (
        <div style={{padding: '24px'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: 16}}>
                <h2 style={{margin: 0}}>工作流绑定</h2>
                <Space>
                    <Button icon={<ReloadOutlined/>} onClick={loadBindings}>刷新</Button>
                    <Button type="primary" icon={<PlusOutlined/>} onClick={handleAdd}>新增绑定</Button>
                </Space>
            </div>
            <Table
                rowKey="id"
                columns={columns}
                dataSource={bindings}
                loading={loading}
                scroll={{x: 'max-content'}}
                pagination={{showSizeChanger: true, showQuickJumper: true}}
            />
            <Modal
                title={editingId ? '编辑绑定' : '新增绑定'}
                open={modalVisible}
                onOk={handleSave}
                onCancel={() => setModalVisible(false)}
            >
                <Form form={form} layout="vertical">
                    <Form.Item name="workflowType" label="工作流类型"
                               rules={[{required: true, message: '请选择'}]}>
                        <Select options={WORKFLOW_TYPES} disabled={!!editingId}/>
                    </Form.Item>
                    <Form.Item name="userId" label="用户ID"
                               rules={[{required: true, message: '请输入用户ID'}]}>
                        <InputNumber style={{width: '100%'}} min={1} disabled={!!editingId}
                                     placeholder="输入用户ID"/>
                    </Form.Item>
                    <Form.Item name="apiConnectionId" label="API连接ID（留空=使用默认）">
                        <InputNumber style={{width: '100%'}} min={1} placeholder="留空使用默认连接"/>
                    </Form.Item>
                    <Form.Item name="isActive" label="启用" valuePropName="checked">
                        <Switch/>
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default WorkflowBindingsPage;
